import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import AppInput from '../../../base/input';
import CustomDropdown from '../../../base/custom-dropdown';
import { isFutureDate } from '../../../../utils/dates.util';

import kybUpdateValidationSchemas from '../../../../constants/schemas/kyb-update';
import {
  useAuthStore,
  useOnboardingStore,
  useRateStore,
  useResourceStore,
} from '../../../../store';
import GooglePlaceAutoComplete from '../../../base/google-place-auto-complete';
import { parseAddressComponents } from '../../../../utils/address.util';
import { IActiveCountry } from '../../../../models/rate.model';

interface BeneficiaryLocationProps {
  index: number; // Pass the index of the beneficiary
}

const BeneficiaryLocation: React.FC<BeneficiaryLocationProps> = ({ index }) => {
  const { getAllCountryStates, countryStates } = useResourceStore();
  const { activeCountries: countries, getActiveCountries: getAllCountries } =
    useRateStore();
  const { businessInfo } = useOnboardingStore();
  const { loadAuthStateFromStorage } = useAuthStore();
  const authState = loadAuthStateFromStorage();
  const { email } = authState || {};

  const filteredCountries = countries.filter(
    (country) =>
      country.name === 'Nigeria' ||
      country.name === 'Ghana' ||
      country.name === 'Senegal' ||
      country.name === 'Cameroon',
  );

  const handleCountryChange = (value: string) => {
    formik.setFieldValue(`[${index}].address.countryCode`, value);
    formik.setFieldTouched(`[${index}].address.countryCode`, true);
    getAllCountryStates(value);
  };

  const handleStateChange = (value: string) => {
    formik.setFieldValue(`[${index}].address.state`, value);
    formik.setFieldTouched(`[${index}].address.state`, true);
  };

  const [manualEntry, setManualEntry] = useState(false);

  const handlePlaceSelected = (place: google.maps.places.PlaceResult) => {
    const address = parseAddressComponents(place);

    formik.setFieldValue(`[${index}].address.streetLine1`, address.streetLine1);
    formik.setFieldValue(`[${index}].address.city`, address.city);
    formik.setFieldValue(`[${index}].address.state`, address.state);
    formik.setFieldValue(`[${index}].address.postalCode`, address.postalCode);
    formik.setFieldValue(`[${index}].address.countryCode`, address.countryCode);
    formik.setFieldValue(`[${index}].address.countryName`, address.countryName);

    formik.setFieldTouched(`[${index}].address.countryCode`, true);
    formik.setFieldTouched(`[${index}].address.state`, true);

    if (address.countryCode) {
      getAllCountryStates(address.countryCode);
    }
  };

  const formik = useFormik({
    initialValues: {
      address: {
        streetLine1: '',
        streetLine2: '',
        city: '',
        state: '',
        postalCode: '',
        countryCode: '',
        countryName: '',
      },
    },
    enableReinitialize: true,
    validationSchema: kybUpdateValidationSchemas[0],
    validateOnMount: true,
    onSubmit: () => {},
  });

  return (
    <div className="p-4 rounded-xl gap-8 flex flex-col border border-[#F1F2F4] w-full h-full bg-white ">
      <div className="grid gap-6">
        <p className="ryno_text_semibold">Beneficiary Location</p>

        <div className="grid grid-cols-1 gap-8">
          {!manualEntry ? (
            <div className="col-span-2">
              <GooglePlaceAutoComplete
                label="Search Address"
                rules="required"
                id={`beneficiary-${index}-address`}
                value={formik.values.address.streetLine1 || ''}
                placeholder="Enter address to search"
                onChange={(value) =>
                  formik.setFieldValue(`address.streetLine1`, value)
                }
                onPlaceSelected={(place) => handlePlaceSelected(place)}
                onBlur={() =>
                  formik.setFieldTouched(`address.streetLine1`, true)
                }
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
              {/* Country */}
              <div>
                <CustomDropdown
                  label="Select Country"
                  options={filteredCountries.map((country: IActiveCountry) => ({
                    name: country.iso3,
                    label: country.name,
                    countryFlag: country.flagUrl,
                  }))}
                  emitObject
                  selectedValue={formik.values.address.countryCode}
                  onSelectChange={(value) => {
                    formik.setFieldValue(`address.countryName`, value.label);
                    handleCountryChange(value.name);
                  }}
                  required
                  showCountryFlag
                  errorMessage={
                    !formik.values.address.countryCode &&
                    formik.touched.address?.countryCode
                      ? 'Country is required'
                      : ''
                  }
                />
              </div>

              {/* State */}
              <div>
                <CustomDropdown
                  label="Select State"
                  options={countryStates.map((state) => ({
                    name: state.code,
                    label: state.name,
                  }))}
                  selectedValue={formik.values.address.state}
                  onSelectChange={(value) => {
                    handleStateChange(value);
                  }}
                  required
                  errorMessage={
                    !formik.values.address.state &&
                    formik.touched.address?.state
                      ? 'State is required'
                      : ''
                  }
                />
              </div>

              {/* City */}
              <div>
                <AppInput
                  type="text"
                  name="city"
                  id="city"
                  onChange={(e) =>
                    formik.setFieldValue(`address.city`, e.target.value)
                  }
                  placeholder="Enter city"
                  required
                  label="City"
                  onBlur={formik.handleBlur}
                  value={formik.values.address.city}
                  errorMessage={
                    formik.touched.address?.city && formik.errors.address?.city
                      ? formik.errors.address?.city
                      : ''
                  }
                />
              </div>

              {/* Postal Code */}
              <div>
                <AppInput
                  type="text"
                  name="postalCode"
                  id="postalCode"
                  onChange={(e) =>
                    formik.setFieldValue(`address.postalCode`, e.target.value)
                  }
                  placeholder="Enter postal code"
                  label="Postal Code"
                  required
                  onBlur={formik.handleBlur}
                  value={formik.values.address.postalCode}
                  errorMessage={
                    formik.touched.address?.postalCode &&
                    formik.errors.address?.postalCode
                      ? formik.errors.address?.postalCode
                      : ''
                  }
                />
              </div>

              {/* Address Line 1 */}
              <div className="col-span-2">
                <AppInput
                  type="text"
                  name="streetLine1"
                  id="streetLine1"
                  onChange={(e) =>
                    formik.setFieldValue(`address.streetLine1`, e.target.value)
                  }
                  placeholder="Enter address line 1"
                  required
                  label="Address 1"
                  onBlur={formik.handleBlur}
                  value={formik.values.address.streetLine1}
                  errorMessage={
                    formik.touched.address?.streetLine1 &&
                    formik.errors.address?.streetLine1
                      ? formik.errors.address?.streetLine1
                      : ''
                  }
                />
              </div>

              {/* Address Line 2 */}
              <div className="col-span-2">
                <AppInput
                  type="text"
                  name="streetLine2"
                  id="streetLine2"
                  onChange={(e) =>
                    formik.setFieldValue(`address.streetLine2`, e.target.value)
                  }
                  placeholder="Enter address line 2"
                  label="Address 2"
                />
              </div>
            </div>
          )}
          <p
            className="text-primary underline text-xs cursor-pointer"
            onClick={() => setManualEntry(!manualEntry)}
          >
            {manualEntry
              ? 'Go back to address search'
              : "Can't find your address? Click here to manually input"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryLocation;
